import { useEffect, useState, useRef } from "react";

// ui
import {
  Box,
  Modal,
} from "@mui/material";

// dnd
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

// API
import { getSaleCompanyGroups, putSaleCompanyChart } from "../api/saleCompanyGroup";
import { getAccountsBySaleCompany } from "../api/account";

// jotai
import { useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

// ローカルstate型定義
type LocalGroup = {
  id: number | null;
  tempId: string;
  sale_company_group_name: string;
  action: "keep" | "create" | "update" | "delete";
};

type LocalAccountAssignment = {
  account_id: number;
  sale_company_group_id: number | null | string;
};

interface SaleCompanyChartProps {
  onModal: boolean;
  swCloseModal: () => void;
  saleCompanyId: string;
  saleCompanyName: string;
}

interface Account {
  id: string;
  account_name: string;
  sale_company_group_id: string | null;
  [key: string]: any;
}

interface Column {
  droppableId: string;
  groupKey: string | null;
  groupName: string;
  accounts: Account[];
}

export function SaleCompanyChart(props: SaleCompanyChartProps) {
  const { onModal, swCloseModal, saleCompanyId, saleCompanyName } = props;
  const setIsLoading = useSetAtom(AppAtoms.isLoading);

  // 変更1: モーダル表示はデータ取得完了後
  const [showModal, setShowModal] = useState(false);

  // 変更2: ローカルstateでグループ・アカウント所属を管理
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [localGroups, setLocalGroups] = useState<LocalGroup[]>([]);
  const [localAccountAssignments, setLocalAccountAssignments] = useState<LocalAccountAssignment[]>([]);

  const [error, setError] = useState<string>("");
  const [updateResult, setUpdateResult] = useState<"" | "success" | "error">("");

  // グループ追加UI
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  // グループ編集UI（グループキーで識別）
  const [editingGroupKey, setEditingGroupKey] = useState<string | null>(null);
  const [editGroupName, setEditGroupName] = useState("");

  const nextTempIdRef = useRef(1);

  useEffect(() => {
    if (onModal && saleCompanyName) {
      loadData();
    } else if (!onModal) {
      setShowModal(false);
    }
  }, [onModal, saleCompanyName]);

  async function loadData() {
    setError("");
    // 変更1: モーダルを非表示のままグローバルローディング表示
    setIsLoading(true);
    nextTempIdRef.current = 1;
    try {
      const [groupsResult, accountsResult] = await Promise.all([
        getSaleCompanyGroups(saleCompanyId),
        getAccountsBySaleCompany(saleCompanyName),
      ]);

      const groups = (groupsResult?.sudDatas ?? []) as {
        id: number;
        sale_company_group_name: string;
      }[];
      const accounts = (accountsResult ?? []) as Account[];

      setAllAccounts(accounts);
      setLocalGroups(
        groups.map((g) => ({
          id: g.id,
          tempId: "",
          sale_company_group_name: g.sale_company_group_name,
          action: "keep" as const,
        }))
      );
      setLocalAccountAssignments(
        accounts.map((a) => ({
          account_id: Number(a.id),
          sale_company_group_id:
            a.sale_company_group_id && a.sale_company_group_id !== "0"
              ? Number(a.sale_company_group_id)
              : null,
        }))
      );

      // 変更1: データ取得完了後にモーダル表示
      setShowModal(true);
    } catch {
      setError("データの取得に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  }

  // グループキー取得（既存グループはid、新規はtempId）
  function getGroupKey(g: LocalGroup): string {
    return g.id !== null ? String(g.id) : g.tempId;
  }

  // 表示用カラムをローカルstateから算出
  const visibleGroups = localGroups.filter((g) => g.action !== "delete");

  const columns: Column[] = [
    {
      droppableId: "unassigned",
      groupKey: null,
      groupName: "未所属",
      accounts: allAccounts.filter((a) => {
        const ass = localAccountAssignments.find(
          (x) => x.account_id === Number(a.id)
        );
        return !ass || ass.sale_company_group_id === null;
      }),
    },
    ...visibleGroups.map((g) => {
      const key = getGroupKey(g);
      return {
        droppableId: key,
        groupKey: key,
        groupName: g.sale_company_group_name,
        accounts: allAccounts.filter((a) => {
          const ass = localAccountAssignments.find(
            (x) => x.account_id === Number(a.id)
          );
          if (!ass) return false;
          if (g.id !== null) {
            return ass.sale_company_group_id === g.id;
          } else {
            return ass.sale_company_group_id === g.tempId;
          }
        }),
      };
    }),
  ];

  // 変更2: DnD操作はローカルstateのみ更新（API呼び出しなし）
  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const srcCol = columns.find((c) => c.droppableId === source.droppableId);
    if (!srcCol) return;
    const movedAccount = srcCol.accounts[source.index];
    if (!movedAccount) return;

    const dstCol = columns.find((c) => c.droppableId === destination.droppableId);
    if (!dstCol) return;

    let newGroupId: number | null | string = null;
    if (dstCol.groupKey !== null) {
      const dstGroup = visibleGroups.find((g) => getGroupKey(g) === dstCol.groupKey);
      if (dstGroup) {
        newGroupId = dstGroup.id !== null ? dstGroup.id : dstGroup.tempId;
      }
    }

    setLocalAccountAssignments((prev) =>
      prev.map((ass) =>
        ass.account_id === Number(movedAccount.id)
          ? { ...ass, sale_company_group_id: newGroupId }
          : ass
      )
    );
  };

  // 変更2: グループ追加はローカルstateのみ
  function handleAddGroupLocal() {
    if (!newGroupName.trim()) return;
    const tempId = `NEW_${nextTempIdRef.current++}`;
    setLocalGroups((prev) => [
      ...prev,
      {
        id: null,
        tempId,
        sale_company_group_name: newGroupName.trim(),
        action: "create",
      },
    ]);
    setNewGroupName("");
    setAddingGroup(false);
  }

  // 変更2: グループ名変更はローカルstateのみ
  function handleEditGroupLocal(groupKey: string) {
    if (!editGroupName.trim()) return;
    setLocalGroups((prev) =>
      prev.map((g) => {
        if (getGroupKey(g) === groupKey) {
          return {
            ...g,
            sale_company_group_name: editGroupName.trim(),
            action: g.action === "keep" ? "update" : g.action,
          };
        }
        return g;
      })
    );
    setEditingGroupKey(null);
    setEditGroupName("");
  }

  // 変更2: グループ削除はローカルstateのみ（所属アカウントは自動で未所属に）
  function handleDeleteGroupLocal(groupKey: string) {
    setLocalAccountAssignments((prev) =>
      prev.map((ass) => {
        const assGroupId = ass.sale_company_group_id;
        const matches =
          typeof assGroupId === "number"
            ? String(assGroupId) === groupKey
            : assGroupId === groupKey;
        if (matches) {
          return { ...ass, sale_company_group_id: null };
        }
        return ass;
      })
    );
    setLocalGroups((prev) =>
      prev.map((g) => {
        if (getGroupKey(g) === groupKey) {
          return { ...g, action: "delete" };
        }
        return g;
      })
    );
  }

  // 変更3: 更新ボタン — 全変更を一括送信
  async function handleUpdate() {
    setShowModal(false);
    swCloseModal();
    setIsLoading(true);
    try {
      await putSaleCompanyChart({
        sale_company_id: Number(saleCompanyId),
        groups: localGroups
          .filter((g) => g.action !== "keep")
          .map((g) => ({
            id: g.id,
            sale_company_group_name: g.sale_company_group_name,
            action: g.action,
            tempId: g.tempId || null,
          })),
        account_assignments: localAccountAssignments.map((a) => ({
          account_id: a.account_id,
          sale_company_group_id: a.sale_company_group_id,
        })),
      });
      setUpdateResult("success");
    } catch {
      setUpdateResult("error");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
    <Modal
      open={updateResult !== ""}
      aria-labelledby="sale-company-chart-result-title"
      data-testid="sale-company-chart-result-modal"
    >
      <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", outline: "none", bgcolor: "background.paper", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-elevated, 0 8px 32px rgba(30,58,95,.12))", padding: "var(--sp-5)", minWidth: "320px", maxWidth: "calc(100vw - 32px)" }}>
        <Box className={`result-state${updateResult === "success" ? " -success" : " -failed"}`}>
          <Box component="span" className="rs-icon">
            {updateResult === "success" ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            )}
          </Box>
          <Box component="span" className="rs-title" id="sale-company-chart-result-title">
            {updateResult === "success" ? "更新が完了しました。" : "更新に失敗しました。"}
          </Box>
          {updateResult !== "success" && (
            <Box component="span" className="rs-desc">再度お試しください。</Box>
          )}
          <button
            className="po-btn po-btn-secondary"
            onClick={() => setUpdateResult("")}
          >
            閉じる
          </button>
        </Box>
      </Box>
    </Modal>
    <Modal open={showModal} aria-labelledby="sale-company-chart-title" data-testid="sale-company-chart-modal">
      <Box
        className="po-modal"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "860px",
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "92vh",
        }}
      >
        <Box className="sip-head">
          <Box
            component="span"
            id="sale-company-chart-title"
            sx={{ fontSize: "var(--fs-16)", fontWeight: "var(--fw-bold)", color: "var(--color-ai)" }}
          >
            体制図
          </Box>
        </Box>

        {error && (
          <Box className="info-alert -warning" sx={{ mx: "var(--sp-5)", mt: "var(--sp-3)" }}>
            <Box component="span" className="ia-ic">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </Box>
            {error}
          </Box>
        )}

        <Box
          className="ce-body"
          sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)", pt: "var(--sp-4)", flex: 1, minHeight: 0 }}
        >
          <Box component="span" className="po-helper">
            {saleCompanyName}の体制図を管理します。カードをドラッグしてグループへ移動できます。
          </Box>
          {editingGroupKey !== null && (
            <Box sx={{ display: "flex", gap: "var(--sp-2)" }}>
              <button
                className="po-btn po-btn-primary"
                data-testid="oc-edit-complete"
                onClick={() => handleEditGroupLocal(editingGroupKey)}
              >
                編集を完了
              </button>
              <button
                className="po-btn po-btn-ghost"
                data-testid="oc-edit-cancel"
                onClick={() => setEditingGroupKey(null)}
              >
                キャンセル
              </button>
            </Box>
          )}
          <DragDropContext onDragEnd={onDragEnd}>
            <Box className="oc-board" sx={{ flex: 1, minHeight: "300px" }}>
              {columns.map((col) => (
                <Box key={col.droppableId} className="oc-col">
                  <Box className="oc-col-head">
                    {col.groupKey !== null && editingGroupKey === col.groupKey ? (
                      <input
                        className="po-input"
                        value={editGroupName}
                        onChange={(e) => setEditGroupName(e.target.value)}
                        style={{ flex: 1, fontSize: "var(--fs-12)", height: "28px", padding: "0 var(--sp-2)" }}
                        autoFocus
                      />
                    ) : (
                      <>
                        <Box component="span">{col.groupName}</Box>
                        <Box component="span" className="rx22">({col.accounts.length})</Box>
                        {col.groupKey !== null && (
                          <Box component="span" className="rx23" sx={{ "&&": { width: "auto", height: "auto" }, display: "flex", alignItems: "center", gap: "var(--sp-1)", marginLeft: "auto" }}>
                            <button
                              className="po-icon-btn"
                              onClick={() => {
                                setEditingGroupKey(col.groupKey as string);
                                setEditGroupName(col.groupName);
                              }}
                              title="グループ名を編集"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                            <button
                              className="po-icon-btn"
                              onClick={() => handleDeleteGroupLocal(col.groupKey as string)}
                              title="グループを削除"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                  <Droppable droppableId={col.droppableId}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: "100px",
                          backgroundColor: snapshot.isDraggingOver
                            ? "var(--state-hover)"
                            : "transparent",
                          borderRadius: "var(--radius-sm)",
                          transition: "background-color 0.2s",
                        }}
                      >
                        {col.accounts.map((account, index) => (
                          <Draggable
                            key={String(account.id)}
                            draggableId={String(account.id)}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="oc-card"
                                sx={{
                                  mb: "var(--sp-1)",
                                  cursor: "grab",
                                  ...(snapshot.isDragging ? {
                                    boxShadow: "var(--shadow-elevated)",
                                    outline: "2px solid var(--color-kohaku-light)",
                                    transform: "rotate(1.5deg)",
                                  } : {}),
                                }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, color: "var(--color-sumi-muted)" }}>
                                  <circle cx="9" cy="6" r="1.5" fill="currentColor"/>
                                  <circle cx="15" cy="6" r="1.5" fill="currentColor"/>
                                  <circle cx="9" cy="12" r="1.5" fill="currentColor"/>
                                  <circle cx="15" cy="12" r="1.5" fill="currentColor"/>
                                  <circle cx="9" cy="18" r="1.5" fill="currentColor"/>
                                  <circle cx="15" cy="18" r="1.5" fill="currentColor"/>
                                </svg>
                                <Box
                                  component="span"
                                  title={account.account_name || `ID: ${account.id}`}
                                  sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                >
                                  {account.account_name || `ID: ${account.id}`}
                                </Box>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              ))}
              {addingGroup ? (
                <Box className="oc-col" data-testid="sale-company-chart-add-form">
                  <Box className="po-field">
                    <label className="po-label">グループ名<span className="po-req">必須</span></label>
                    <input
                      className="po-input"
                      placeholder="グループ名を入力"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddGroupLocal();
                      }}
                      autoFocus
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: "var(--sp-2)", mt: "var(--sp-2)" }}>
                    <button className="po-btn po-btn-primary" onClick={handleAddGroupLocal}>
                      追加
                    </button>
                    <button
                      className="po-btn po-btn-ghost"
                      onClick={() => {
                        setAddingGroup(false);
                        setNewGroupName("");
                      }}
                    >
                      キャンセル
                    </button>
                  </Box>
                </Box>
              ) : (
                <button
                  className="oc-add"
                  data-testid="oc-add-button"
                  onClick={() => setAddingGroup(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: "block", margin: "0 auto 4px" }}>
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  グループ追加
                </button>
              )}
            </Box>
          </DragDropContext>
        </Box>

        <Box className="sip-foot">
          <button
            className="po-btn po-btn-secondary"
            onClick={() => {
              setShowModal(false);
              swCloseModal();
            }}
          >
            キャンセル
          </button>
          <button
            className="po-btn po-btn-primary"
            data-testid="sale-company-chart-save-button"
            onClick={handleUpdate}
          >
            更新
          </button>
        </Box>
      </Box>
    </Modal>
    </>
  );
}
